<script setup lang="ts">
import { computed } from 'vue'
import type { Validation } from '@vuelidate/core'
import { VInfo } from '@/modules/shared/components'

const props = defineProps<{
  label?: string
  description?: string
  info?: string
  property?: string
  v$?: Validation
  error?: string
  trueLabel?: string
  falseLabel?: string
  disabled?: boolean
}>()

const value = defineModel<boolean>({ required: true })
const error = computed(() => {
  if (props.error) return props.error
  if (!props.property) return undefined
  return props.v$?.[props.property]?.$errors?.[0]?.$message
})
const hasError = computed(() => Boolean(error.value))
const errorId = computed(() => (error.value && props.property ? `${props.property}-error` : undefined))
const infoId = computed(() => (props.info && props.property ? `${props.property}-info` : undefined))
const describedBy = computed(() => [errorId.value, infoId.value].filter(Boolean).join(' ') || undefined)
const dividerClass = computed(() => (hasError.value ? 'border-red-200' : 'border-gray-200'))

const onClick = (newValue: boolean) => {
  if (props.disabled) return
  value.value = newValue
}

const fieldClasses = computed(() => [
  'mt-3 flex w-full overflow-hidden rounded-md border bg-white shadow-sm',
  props.disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '',
  hasError.value ? 'border-red-300' : 'border-gray-300',
])

const optionClasses = (isActive: boolean, isFirst: boolean) => [
  'relative flex-1 px-3 py-2 text-center text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 shadow-sm',
  props.disabled
    ? 'cursor-not-allowed text-gray-400'
    : [
        'cursor-pointer text-gray-700 hover:shadow-md active:shadow-sm',
        isActive ? 'hover:bg-sky-50' : 'hover:bg-gray-50',
      ],
  isActive ? (props.disabled ? 'bg-gray-100' : 'bg-[#3783B0]/10 text-gray-900') : 'bg-white',
  isFirst ? `border-r ${dividerClass.value}` : '',
  hasError.value ? 'focus-visible:ring-red-200' : 'focus-visible:ring-sky-200',
]
</script>

<template>
  <fieldset :id="property" :aria-describedby="describedBy" class="rounded-lg border border-gray-300/50 bg-white p-4">
    <label v-if="label" class="block cursor-pointer text-sm font-medium text-gray-700">
      <span class="inline-flex items-center gap-2"
        >{{ label }} <VInfo v-if="info" :text="info" :tooltip-id="infoId"
      /></span>
    </label>
    <p v-if="description" class="mt-1 text-sm text-gray-500">{{ description }}</p>
    <div class="w-full">
      <div :class="fieldClasses" role="group" :aria-invalid="hasError || undefined">
        <button
          type="button"
          :class="optionClasses(value === true, true)"
          :disabled="disabled"
          :aria-pressed="value === true"
          @click="onClick(true)"
        >
          {{ trueLabel || 'Yes' }}
        </button>
        <button
          type="button"
          :class="optionClasses(value === false, false)"
          :disabled="disabled"
          :aria-pressed="value === false"
          @click="onClick(false)"
        >
          {{ falseLabel || 'No' }}
        </button>
      </div>
    </div>
    <p v-if="error" :id="errorId" class="mt-1 text-sm text-red-500">{{ error }}</p>
  </fieldset>
</template>
